import { PromptCategory } from "@redotech/redo-model/marketing/marketing-agent-prompt/marketing-agent-prompt-definition";
import type { RouteObject } from "react-router-dom";

const CATEGORIES = [
  PromptCategory.STRATEGY,
  PromptCategory.TRIGGER,
  PromptCategory.GOAL,
  PromptCategory.METHOD,
];

function createPromptRoutes(category: PromptCategory): RouteObject[] {
  const prefix = category;
  const label = category.charAt(0).toUpperCase() + category.slice(1);
  const basePath = `${prefix}/`;

  return [
    {
      path: `${basePath}create`,
      handle: { breadcrumb: `Create ${label}` },
      async lazy() {
        const { MarketingAgentPromptPage } =
          await import("./marketing-agent-prompt-page");
        return {
          element: (
            <MarketingAgentPromptPage category={category} mode="create" />
          ),
        };
      },
    },
    {
      path: `${basePath}view/:promptId`,
      handle: { breadcrumb: `View ${label}` },
      async lazy() {
        const { MarketingAgentPromptPage } =
          await import("./marketing-agent-prompt-page");
        return {
          element: <MarketingAgentPromptPage category={category} mode="view" />,
        };
      },
    },
    {
      path: `${basePath}edit/:promptId`,
      handle: { breadcrumb: `Edit ${label}` },
      async lazy() {
        const { MarketingAgentPromptPage } =
          await import("./marketing-agent-prompt-page");
        return {
          element: <MarketingAgentPromptPage category={category} mode="edit" />,
        };
      },
    },
  ];
}

export const marketingRoutes: RouteObject[] = [
  {
    handle: { breadcrumb: "Marketing" },
    async lazy() {
      const { Page } = await import("../app/page");
      return { element: <Page hidePadding={() => true} /> };
    },
    children: [
      {
        path: "campaign-timeline",
        handle: { breadcrumb: "Campaign Timeline" },
        async lazy() {
          const { CampaignTimelinePage } =
            await import("./campaign-timeline-page");
          return { Component: CampaignTimelinePage };
        },
      },
      {
        path: "email-corrections",
        handle: { breadcrumb: "Email Corrections" },
        async lazy() {
          const { EmailCorrectionsPage } =
            await import("./email-corrections-page");
          return { Component: EmailCorrectionsPage };
        },
      },
      {
        path: "amp-status",
        handle: { breadcrumb: "AMP Status" },
        async lazy() {
          const { AmpStatusPage } = await import("./amp-status-page");
          return { Component: AmpStatusPage };
        },
      },
      {
        path: "phone-numbers",
        handle: { breadcrumb: "Phone Numbers" },
        async lazy() {
          const { PhoneNumbersPage } = await import("./phone-numbers-page");
          return { Component: PhoneNumbersPage };
        },
      },
      {
        path: "recover",
        handle: { breadcrumb: "Recover" },
        async lazy() {
          const { RecoverPage } = await import("./pages/recover/recover-page");
          return { Component: RecoverPage };
        },
      },
      {
        path: "marketing-agent",
        handle: { breadcrumb: "Marketing Agent" },
        async lazy() {
          const { MarketingAgentPage } =
            await import("./pages/marketing-agent/marketing-agent-page");
          return { Component: MarketingAgentPage };
        },
      },
      {
        path: "concierge",
        handle: { breadcrumb: "Concierge" },
        children: [
          {
            index: true,
            async lazy() {
              const { ConciergePage } =
                await import("./pages/concierge/concierge-page");
              return { Component: ConciergePage };
            },
          },
          {
            path: "ab-testing",
            handle: { breadcrumb: "A/B Testing" },
            async lazy() {
              const { ConciergeAbTestingPage } =
                await import("./pages/concierge-ab-testing/concierge-ab-testing-page");
              return { Component: ConciergeAbTestingPage };
            },
          },
        ],
      },
      {
        path: "ai",
        handle: { breadcrumb: "AI" },
        async lazy() {
          const { AiPage } = await import("./ai-page");
          return { Component: AiPage };
        },
        children: CATEGORIES.flatMap((category) =>
          createPromptRoutes(category),
        ),
      },
      {
        path: "shipment-tracking",
        handle: { breadcrumb: "Shipment Tracking" },
        async lazy() {
          const { ShipmentTrackingPage } =
            await import("./pages/shipment-tracking/shipment-tracking-page");
          return { Component: ShipmentTrackingPage };
        },
      },
      {
        path: "se-dashboard",
        handle: { breadcrumb: "SE Dashboard" },
        async lazy() {
          const { SeDashboardPage } =
            await import("./pages/se-dashboard/se-dashboard-page");
          return { Component: SeDashboardPage };
        },
      },
      {
        path: "debug-email-template",
        handle: { breadcrumb: "Debug Email Template" },
        async lazy() {
          const { DebugEmailTemplatePage } =
            await import("./debug-email-template-page");
          return { Component: DebugEmailTemplatePage };
        },
      },
      {
        path: "signups",
        handle: { breadcrumb: "Signup form migration" },
        children: [
          {
            index: true,
            async lazy() {
              const { SignupsTeamsPage } =
                await import("./pages/signups/teams-page");
              return { Component: SignupsTeamsPage };
            },
          },
          {
            path: ":teamId",
            handle: { breadcrumb: "Team" },
            async lazy() {
              const { SignupsTeamDetailPage } =
                await import("./pages/signups/team-detail-page");
              return { Component: SignupsTeamDetailPage };
            },
          },
        ],
      },
    ],
  },
];
